import { FieldType, StepT, StepType } from "../../Stepper/Step/types";
import { mapProviderToDisplayName } from "../../ProviderDropdown/utils";
import { ProviderWithLogo } from "../../ProviderWithLogo";

import { CustomProvider } from "@/queries/providers/types";

export const ALLOWED_PROVIDERS = ["openai", "anthropic", "cohere"];

export const AUTH_SCHEME_OPTIONS = [
  {
    title: "Authorization: Bearer (default)",
    label: "Authorization: Bearer (default)",
    value: "bearer",
    id: "bearer",
  },
  {
    title: "X-API-Key header",
    label: "X-API-Key header",
    value: "x-api-key",
    id: "x-api-key",
  },
  {
    title: "Custom headers",
    label: "Custom headers",
    value: "custom",
    id: "custom",
  },
];

const getProviderStepTitle = (isEdit: boolean, isPreview: boolean): string => {
  if (isEdit) {
    return "Edit Custom Provider";
  }

  if (isPreview) {
    return "Preview Custom Provider";
  }

  return "Create Custom Provider";
};

const getProviderStepDescription = (isEdit: boolean, isPreview: boolean): string => {
  if (isEdit) {
    return "Edit the custom provider for your workspace.";
  }

  if (isPreview) {
    return "Preview the custom provider for your workspace.";
  }

  return "Create a custom provider for your workspace.";
};

const validateExtraHeadersJson = (value: string): string | true => {
  if (!value) {
    return "Custom headers require a JSON object";
  }
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return 'Custom headers must be a JSON object (e.g. { "X-My-Header": "value" })';
    }
    const entries = Object.entries(parsed);
    if (entries.length === 0) {
      return "Custom headers must contain at least one entry";
    }
    if (entries.length > 10) {
      return "Custom headers may contain at most 10 entries";
    }
    for (const [key, val] of entries) {
      if (!key || typeof val !== "string") {
        return "Header values must be strings";
      }
    }
    return true;
  } catch {
    return "Custom headers must be valid JSON";
  }
};

export const getCustomProviderSteps = (
  providers: CustomProvider[],
  isPreview: boolean,
  isEdit: boolean,
) => {
  const title = getProviderStepTitle(isEdit, isPreview);
  const description = getProviderStepDescription(isEdit, isPreview);

  return [
    {
      id: "create_custom_provider",
      title,
      description,
      isOptional: false,
      type: StepType.FIELDS,
      options: [
        {
          readOnly: isPreview,
          label: "Name",
          id: "name",
          tooltip: "This is a human-readable name for the provider",
          type: FieldType.TEXT,
          placeholder: "Enter the name of the provider",
        },
        {
          readOnly: isPreview,
          label: "Client",
          id: "client",
          type: FieldType.AUTOCOMPLETE,
          placeholder: "Example: OpenAI",
          addNewOption: false,
          tooltip: "Select the client for your provider base URL",
          fieldOptions: providers
            .filter(item => !item.workspace_id && ALLOWED_PROVIDERS.includes(item.name))
            .map(provider => ({
              label: mapProviderToDisplayName(provider.name),
              value: provider.name,
              id: provider.name,
            })),
          renderOption: value => <ProviderWithLogo provider={value.value} />,
        },
        {
          readOnly: isPreview,
          label: "Base URL",
          id: "base_url",
          type: FieldType.URL,
          placeholder: "Enter the base URL of the provider",
        },
        {
          readOnly: isPreview,
          label: "API Key",
          id: "api_key",
          type: FieldType.TEXT,
          placeholder: "Enter the API key of the provider",
          inputType: "password",
        },
        {
          readOnly: isPreview,
          label: "Auth scheme",
          id: "auth_scheme",
          type: FieldType.SELECT,
          tooltip:
            'How the API key is sent on the wire. Use "X-API-Key header" for proxies that reject `Authorization: Bearer …`. Use "Custom headers" to send arbitrary headers (e.g. for HMAC, multi-header auth).',
          placeholder: "Select an auth scheme",
          fieldOptions: AUTH_SCHEME_OPTIONS,
          fullWidth: true,
        },
        {
          readOnly: isPreview,
          label: "Custom headers (JSON)",
          id: "extra_headers",
          type: FieldType.TEXTAREA,
          placeholder: '{ "X-My-Header": "value" }',
          tooltip:
            "JSON object of headers to send on every request. The OpenAI SDK's default Authorization header is stripped.",
          showIf: selected => selected.auth_scheme === "custom",
          validation: value => validateExtraHeadersJson(value),
        },
      ],
    },
  ] as StepT[];
};
